import { useQuery, UseQueryResult } from "react-query";
import { IUser } from "../../../services/admin/admin.service";
import { PaginatedApiResponse } from "../../../types/pagination.type";
import { hasAllowedRoles } from "../../../helpers";
import { useContext } from "react";
import { AuthContext } from "../../../contexts";
import { SelectOption } from "../../../components/form/SelectWithValidation/SelectWithValidation";
import userService from "../../../services/user/user.service";
import { uuid } from "../../../types";
import { Role } from "../../../types/user.type";

// This is a custom hook that fetches users from the API and returns them as select options.
// provide filter_out_users_from_org_id to filter out users from the given organisation
// Only for admin and super admin users
export const useUserSelectOptions = (options: {
  user_search_term?: string;
  filter_out_users_from_org_id?: uuid;
  query_key?: string;
}): UseQueryResult<SelectOption<IUser>[]> => {
  const { user: signed_user } = useContext(AuthContext);
  const is_privileged_user = hasAllowedRoles(signed_user?.roles, {
    allowed: [Role.Admin, Role.SuperAdmin],
  });

  const fetchUsers = async (): Promise<SelectOption<IUser>[]> => {
    let res: PaginatedApiResponse<IUser>;
    if (is_privileged_user) {
      if (!options.user_search_term) {
        res = await userService.getAllUsers(
          0,
          50,
          options?.filter_out_users_from_org_id,
        );
      } else {
        res = await userService.getAllUsersBySearchTerm(
          options.user_search_term,
          0,
          25,
          options?.filter_out_users_from_org_id,
        );
      }
      return (
        res?.hits?.map(user => ({
          label: `${user.firstname} ${user.lastname}`,
          value: user.id,
          meta: user,
          description: user.email,
        })) || []
      );
    } else {
      return []; // only admins have access to all users APIs
    }
  };

  return useQuery({
    queryKey: [
      "user_select_options",
      options?.user_search_term,
      signed_user?.id,
      `filter-users-from-org-${JSON.stringify(
        options.filter_out_users_from_org_id,
      )}`,
      options.query_key,
    ],
    queryFn: fetchUsers,
  });
};
